import React, { useEffect, useState } from "react";
import { ArrowUpRight, Loader2, Check, Crown } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LanguageContext";

export const UpgradeCTA = ({ variant = "card" }) => {
  const { user } = useAuth();
  const { lang, t } = useLang();
  const [tiers, setTiers] = useState([]);
  const [loadingKey, setLoadingKey] = useState(null);

  useEffect(() => {
    api.get("/membership-tiers").then((r) => setTiers(r.data)).catch(() => {});
  }, []);

  if (!user || user.role === "admin" || !tiers.length) return null;

  const current = tiers.find((tr) => tr.key === user.tier);
  const currentPrice = current?.price ?? 0;
  const upgrades = tiers.filter((tr) => (tr.price ?? 0) > currentPrice).sort((a, b) => a.price - b.price);
  if (!upgrades.length) return null;

  const targetTier = upgrades[0];

  const startUpgrade = async (tierKey) => {
    setLoadingKey(tierKey);
    try {
      const { data } = await api.post("/payments/checkout", {
        tier_key: tierKey, origin_url: window.location.origin, user_id: user.id,
      });
      if (data.checkout_url) { window.location.href = data.checkout_url; return; }
      if (data.free) { toast.info("That tier is free."); return; }
      toast.info(data.message || "Please try again.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not start upgrade");
    } finally {
      setLoadingKey(null);
    }
  };

  const name = lang === "fr" ? targetTier.name_fr : targetTier.name_en;
  const benefits = (lang === "fr" ? targetTier.benefits_fr : targetTier.benefits_en) || [];

  if (variant === "banner") {
    return (
      <div data-testid="upgrade-cta-banner" className="flex items-center justify-between gap-4 flex-wrap rounded-2xl bg-secondary/10 border border-secondary/20 px-5 py-4">
        <p className="text-sm text-primary/90 flex items-center gap-2">
          <Crown size={16} className="text-secondary" />
          {t("Unlock more with", "Débloquez plus avec")} <b>{name}</b> — ${targetTier.price}/{t("mo", "mois")}
        </p>
        <button data-testid="upgrade-cta-btn" onClick={() => startUpgrade(targetTier.key)} disabled={loadingKey === targetTier.key}
          className="inline-flex items-center gap-2 rounded-full bg-secondary text-secondary-foreground px-5 py-2 text-sm font-semibold hover:bg-primary transition-colors disabled:opacity-60">
          {loadingKey === targetTier.key ? <Loader2 size={15} className="animate-spin" /> : <ArrowUpRight size={15} />}
          {t("Upgrade", "Améliorer")}
        </button>
      </div>
    );
  }

  return (
    <div data-testid="upgrade-cta-card" className="relative overflow-hidden rounded-[2rem] bg-primary text-primary-foreground grain p-8 md:p-10">
      <div className="absolute -top-16 -right-10 w-64 h-64 rounded-full bg-secondary/30 blur-3xl blob" />
      <div className="relative">
        <p className="text-xs uppercase tracking-[0.25em] text-accent font-semibold flex items-center gap-2"><Crown size={14} /> {t("Upgrade your membership", "Améliorez votre adhésion")}</p>
        <h3 className="font-display text-3xl md:text-4xl font-medium mt-2">{t("Step up to", "Passez à")} {name}</h3>
        <p className="text-primary-foreground/70 mt-2">${targetTier.price}<span className="text-primary-foreground/50">/{t("mo", "mois")}</span> · {t("cancel anytime", "annulez à tout moment")}</p>
        <ul className="mt-5 grid sm:grid-cols-2 gap-x-6 gap-y-2">
          {benefits.slice(0, 4).map((b, i) => (
            <li key={i} className="flex gap-2 text-sm text-primary-foreground/85"><Check size={16} className="text-secondary shrink-0 mt-0.5" /> {b}</li>
          ))}
        </ul>
        <div className="flex flex-wrap gap-3 mt-7">
          {upgrades.map((tr) => (
            <button key={tr.key} data-testid={`upgrade-cta-btn-${tr.key}`} onClick={() => startUpgrade(tr.key)} disabled={loadingKey === tr.key}
              className="inline-flex items-center gap-2 rounded-full bg-secondary text-secondary-foreground px-6 py-3 font-semibold hover:bg-accent hover:text-primary transition-colors disabled:opacity-60">
              {loadingKey === tr.key ? <Loader2 size={16} className="animate-spin" /> : <ArrowUpRight size={16} />}
              {t("Upgrade to", "Passer à")} {lang === "fr" ? tr.name_fr : tr.name_en} — ${tr.price}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default UpgradeCTA;
